import React from 'react';
import {Controller, useForm} from 'react-hook-form';
import {TextInput} from '@mantine/core';
import {Divider} from 'src/design/atoms/Divider';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import type {EmailActionMetadata, NotificationAttributeEnum} from 'src/api/ProactApiClient';
import {PersonnelCheckboxList} from 'src/app/databound-elements/PersonnelCheckboxList';
import {UserGroupCheckboxList} from 'src/app/databound-elements/UserGroupCheckboxList';
import {Checkbox} from 'src/design/atoms/Checkbox';
import {CheckboxList} from 'src/design/atoms/CheckboxList';
import {FormRichTextEditorV2} from 'src/app/forms/FormRichTextEditor/FormRichTextEditorV2';
import {SecondaryPanel} from 'src/design/atoms/SecondaryPanel';
import type {IFormFieldV2} from 'src/types/FormV2';
import {WorkflowTargetAttributes} from 'src/app/workflows/workflowHelpers';
import {WorkflowNotificationAttributeSection} from 'src/app/workflows/WorkflowNotificationAttributeSection';
import type {ActionEditorProps} from './ActionCard';
import {SectionLabel} from './SectionLabel';

const bodyField: IFormFieldV2 = {
  name: 'body',
  label: 'Body',
  type: 'richtext',
  description:
    'Variables can be used to add dynamic data to this field. Refer to the variables lists above for more information.',
};

interface EmailFormValues {
  subject: string;
  body: string;
  emailPlaintext: boolean;
  filterByTriggererPrimaryProject: boolean;
  toTargetingAttributes: string[];
  toUserGroupIds: string[];
  toUserIds: string[];
}

const toFormValues = (
  meta: EmailActionMetadata | undefined
): EmailFormValues => ({
  subject: meta?.subject ?? '',
  body: meta?.body ?? '',
  emailPlaintext: meta?.emailPlaintext ?? true,
  filterByTriggererPrimaryProject:
    meta?.filterByTriggererPrimaryProject ?? false,
  toTargetingAttributes: meta?.toTargetingAttributes?.map(t => String(t)) ?? [],
  toUserGroupIds: meta?.toUserGroupIds?.map(i => String(i)) ?? [],
  toUserIds: (meta?.toUserIds ?? []).map(i => String(i)),
});

export const ActionEmailEditor: React.FC<ActionEditorProps> = ({
  action,
  onActionChange,
  metadata,
}) => {
  const {isScheduled} = metadata;
  const meta = action.metadata?.emailMetadata;

  // `values` (not `defaultValues`) so external metadata replacement — API
  // reload, action list changes — flows back into the form. User-driven
  // changes mirror into action.metadata and come back deep-equal, so they
  // don't trigger a reset.
  const {control} = useForm<EmailFormValues>({
    values: toFormValues(meta),
  });

  const mirrorEmailMetadataChange = (partial: Partial<EmailActionMetadata>) => {
    onActionChange({
      ...action,
      metadata: {
        ...action.metadata,
        emailMetadata: {
          ...action.metadata?.emailMetadata,
          ...partial,
        },
      },
    });
  };

  return (
    <Stack gap="md">
      <SectionLabel
        label="Notification Targets"
        description="Choose who to send this notification to. Configure targets by user groups, data attributes, or individual users."
        withSeparation={false}
      />

      <SecondaryPanel>
        <Text fw={600} size="sm">
          Send To
        </Text>
        <Divider />
        <Controller
          name="toTargetingAttributes"
          control={control}
          render={({field}) => (
            <CheckboxList
              label="Send to Attribute"
              disabled={isScheduled}
              values={WorkflowTargetAttributes}
              selected={field.value}
              onChange={values => {
                field.onChange(values);
                mirrorEmailMetadataChange({
                  toTargetingAttributes: values.map(
                    s => Number(s) as NotificationAttributeEnum
                  ),
                });
              }}
              description={
                isScheduled
                  ? 'This field cannot be used with a Scheduled trigger. Please choose another trigger type to use this property.'
                  : undefined
              }
            />
          )}
        />
        <Controller
          name="toUserGroupIds"
          control={control}
          render={({field}) => (
            <UserGroupCheckboxList
              label="Send To Groups"
              showNotificationsGroups={true}
              selected={field.value}
              onChange={values => {
                field.onChange(values);
                mirrorEmailMetadataChange({
                  toUserGroupIds: values.map(
                    s => Number(s) as NotificationAttributeEnum
                  ),
                });
              }}
            />
          )}
        />
        <Controller
          name="toUserIds"
          control={control}
          render={({field}) => (
            <PersonnelCheckboxList
              label="Send to Users"
              selected={field.value}
              onChange={values => {
                field.onChange(values);
                mirrorEmailMetadataChange({
                  toUserIds: values.map(i => +i),
                });
              }}
            />
          )}
        />
      </SecondaryPanel>

      <WorkflowNotificationAttributeSection
        action={action}
        metadata={metadata}
      />

      <SectionLabel label="Compose" />

      <Controller
        name="subject"
        control={control}
        render={({field}) => (
          <TextInput
            label="Subject"
            description="Variables can be used to add dynamic data to this field. Refer to the variables lists above for more information."
            value={field.value}
            onChange={e => {
              field.onChange(e);
              mirrorEmailMetadataChange({subject: e.target.value});
            }}
            onBlur={field.onBlur}
          />
        )}
      />

      <Controller
        name="body"
        control={control}
        render={({field}) => (
          <FormRichTextEditorV2
            field={bodyField}
            value={field.value}
            onChange={(_name, value) => {
              field.onChange(value);
              mirrorEmailMetadataChange({body: value});
            }}
            disableExternalContent={true}
          />
        )}
      />

      <Controller
        name="filterByTriggererPrimaryProject"
        control={control}
        render={({field}) => (
          <Checkbox
            label="Limit recipients to triggerer's primary project"
            checked={field.value}
            onChange={(_e, checked) => {
              field.onChange(checked);
              mirrorEmailMetadataChange({
                filterByTriggererPrimaryProject: checked,
              });
            }}
          />
        )}
      />

      <Controller
        name="emailPlaintext"
        control={control}
        render={({field}) => (
          <Checkbox
            label="Send as plaintext email?"
            checked={field.value}
            onChange={(_e, checked) => {
              field.onChange(checked);
              mirrorEmailMetadataChange({
                emailPlaintext: checked,
              });
            }}
          />
        )}
      />
    </Stack>
  );
};
